import * as React from "react"
import { cn } from "@merels/ui"
import { mateListTestIds } from "./mateList.testIds"
import { Team } from "../Team/Team"
import { MateCard } from "../../mateCard/MateCard/MateCard"
import { useTheme } from "../../../../contexts/ThemeContext"
import { useSignals } from "@preact/signals-react/runtime"
import type { MateListVM, MateListVMMate, MateListVMPlayer } from "../../../../deps/ui/mateListVM/mateListVM.types"
import { AddBotButton } from "../../controls/AddBotButton/AddBotButton"
import { SafePlayerPiece } from "../../../gameProvided/SafePlayerPiece"

export type Mate = MateListVMMate
export type Player = MateListVMPlayer

export interface MateListProps {
  mateListVM: MateListVM
  /** When true, no seat is highlighted as having the active turn. */
  isGameOver?: boolean
  /** Present only when the caller wants clicking our own mate's tile to open its settings. */
  onOurMateClick?: () => void
}

export const MateList = React.forwardRef<HTMLDivElement, MateListProps>(
  ({ mateListVM, isGameOver = false, onOurMateClick }, ref) => {
    useSignals()
    const { getPlayerColor, playerPiece: PlayerPiece } = useTheme()

    const players = mateListVM.players.value
    const playerDisplayOrder = mateListVM.playerDisplayOrder.value
    const observers = mateListVM.observers.value
    // >0 only when Omni mode is active and both an Omni mate and a real mate are
    // both actually present among `observers` - see mateListVM.ts's omniObserverCount.
    const observersSeparatorIndex = mateListVM.omniObserverCount.value || undefined
    const ourMateId = mateListVM.ourMateId.value
    const hasActiveGame = mateListVM.hasActiveGame.value
    const hidePlayerLabels = mateListVM.hidePlayerLabels.value
    // Room-wide (not mateListVM.isOmniMode, this client's own toggle) - the
    // "playing as" cues below reflect whichever Omni mate *this* client is
    // acting as, which any connected client may now do, not just the one
    // that turned Omni mode on.
    const isOmniMode = mateListVM.hasAnyOmniMate.value
    const activePlayerId = mateListVM.activePlayerId.value
    const currentPlayerId = mateListVM.currentPlayerId.value
    const onPlayerSelect = mateListVM.isPlayerSelectionEnabled.value
      ? (playerId: string) => mateListVM.selectPlayer(playerId)
      : undefined
    const onAddAIPlayer = mateListVM.addAIPlayer

    // `fallbackBorderColor` is only used for the observers team.
    // In the special 2-player Omni-mode color mode we tint the observers border with the
    // current player's primary color at low opacity.
    const observersFallbackBorderColor = (() => {
      if (!mateListVM.omniMode.shouldUsePlayerColors.value) return undefined
      const idx = mateListVM.omniMode.currentPlayerIndex.value
      if (idx === null) return undefined
      const currentPlayerColor = getPlayerColor(idx)
      return currentPlayerColor ? `${currentPlayerColor.primary}33` : undefined // 20% opacity
    })()

    return (
      <div
        ref={ref}
        className="flex flex-col flex-1 min-h-0 overflow-hidden"
      >
        <div className="flex-1 overflow-y-auto p-2">
          {hasActiveGame && playerDisplayOrder.map((playerIndex) => {
            const player = players[playerIndex]
            let playerColor = getPlayerColor(playerIndex)
            
            // getPlayerColor already handles defaults, so playerColor should always be set
            if (!playerColor) {
              // This shouldn't happen, but provide a safe fallback
              playerColor = { primary: '#999999', background: '#f0f0f0', foreground: '#000000' }
            }
            
            // Get border color from game theme or defaults
            const borderColor = playerColor?.primary
            
            // Active turn: bold label (no border pulse). Cleared when the game ends.
            const isActiveTurn = Boolean(!isGameOver && activePlayerId && activePlayerId === player.id)
            // Current player indicator (Omni mode): arrow in label.
            const isPlayingAs = Boolean(currentPlayerId && currentPlayerId === player.id)
            // Use primary (player identity color), not foreground (contrast text for
            // player backgrounds we no longer paint on the group).
            const textColor = playerColor.primary
            
            return (
              <Team
                key={player.id} 
                data-testid={`${mateListTestIds.playerBox}-${player.id}`}
                mates={player.mates}
                ourMateId={ourMateId}
                onOurMateClick={onOurMateClick}
                onDismissBot={mateListVM.dismissBot}
                onToggleHost={mateListVM.toggleHost}
                onActAs={mateListVM.actAs}
                className="mb-4"
                borderColor={borderColor}
                isInteractive={Boolean(onPlayerSelect)}
                onClick={() => onPlayerSelect?.(player.id)}
                headerWrapperClassName="flex items-center justify-end gap-2"
                header={hidePlayerLabels ? undefined : (
                  <>
                    <span
                      className={cn(
                        "text-xs text-right inline-flex items-center justify-end gap-1",
                        !PlayerPiece && "uppercase",
                        isActiveTurn
                          ? "font-bold"
                          : "font-medium"
                      )}
                      style={PlayerPiece ? undefined : { color: textColor }}
                      aria-label={player.displayName || player.id}
                    >
                      {isOmniMode && isPlayingAs && (
                        <span className="animate-blink inline-block" aria-hidden="true">
                          ⇒
                        </span>
                      )}
                      {PlayerPiece ? (
                        <SafePlayerPiece
                          component={PlayerPiece}
                          playerIndex={playerIndex}
                          playerId={player.id}
                          size={16}
                        />
                      ) : (
                        player.displayName || player.id
                      )}
                    </span>
                  </>
                )}
              />
            )
          })}

          {hasActiveGame && observers.length > 0 && (
            <Team
              className="mb-4"
              mates={observers}
              separatorIndex={observersSeparatorIndex}
              ourMateId={ourMateId}
              onOurMateClick={onOurMateClick}
              onDismissBot={mateListVM.dismissBot}
              onToggleHost={mateListVM.toggleHost}
              onActAs={mateListVM.actAs}
              fallbackBorderColor={observersFallbackBorderColor}
              header={
                <span 
                  className={cn(
                    "text-xs font-medium uppercase text-right block",
                    isOmniMode && players.length === 2 && currentPlayerId
                      ? undefined
                      : "text-muted"
                  )}
                  style={
                    isOmniMode && players.length === 2 && currentPlayerId
                      ? (() => {
                          const currentPlayerIndex = players.findIndex(p => p.id === currentPlayerId)
                          const currentPlayerColor = currentPlayerIndex >= 0 ? getPlayerColor(currentPlayerIndex) : null
                          return currentPlayerColor ? { color: currentPlayerColor.foreground } : undefined
                        })()
                      : undefined
                  }
                >
                  Observers
                </span>
              }
            />
          )}

          {!hasActiveGame && observers.length > 0 && (
            <div className="space-y-2">
              {observers.map((mate, index) => (
                <React.Fragment key={mate.id}>
                  {index === observersSeparatorIndex && <div className="border-t border-basic/40" />}
                  <MateCard
                    mate={mate}
                    isOurMate={mate.id === ourMateId}
                    botDisplayName={mate.botDisplayName}
                    noBackground={mate.source === 'shared-screen'}
                    onClick={onOurMateClick}
                    onActAs={mate.canActAs ? () => mateListVM.actAs(mate.id) : undefined}
                    onToggleHost={mate.canToggleHost && mateListVM.toggleHost ? () => mateListVM.toggleHost?.(mate.id) : undefined}
                  />
                </React.Fragment>
              ))}
            </div>
          )}

          {players.length === 0 && observers.length === 0 && (
            <div 
              className={cn(
                "text-sm p-4 text-center",
                (isOmniMode) && currentPlayerId
                  ? undefined // Use inline style for player foreground color
                  : "text-muted"
              )}
              style={
                (isOmniMode) && currentPlayerId
                  ? (() => {
                      const currentPlayerIndex = players.findIndex(p => p.id === currentPlayerId)
                      const currentPlayerColor = currentPlayerIndex >= 0 ? getPlayerColor(currentPlayerIndex) : null
                      return currentPlayerColor ? { color: currentPlayerColor.foreground } : undefined
                    })()
                  : undefined
              }
            >
              No mates
            </div>
          )}
        </div>
        
        {/* Add Bot button - shown when game supports bots and is in setup or active game */}
        {mateListVM.showAddBot.value && onAddAIPlayer && (
          <AddBotButton
            onClick={onAddAIPlayer}
            data-testid={mateListTestIds.addAIPlayerButton}
          />
        )}
      </div>
    )
  }
)
MateList.displayName = "MateList"
